import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {ContainerClusterEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Anchor, Box, Modal, ModalProps} from "@mantine/core";
import {Link} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useContainerClusterDataTableFilter} from "../ContainerClusterDataTable/useContainerClusterDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: ContainerClusterEntity[] = []

export type ContainerClusterDataTableProps = EntityDataTableProps<ContainerClusterEntity> & {}

export const ContainerClusterDataTable = (props: ContainerClusterDataTableProps) => {
  const {
    records: containerClusters = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useContainerClusterDataTableFilter({
    records: containerClusters
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
      "siteName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.siteName?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<ContainerClusterEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.containercluster.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record} />
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "site"),
        title: t("common.entity.containercluster.table.column.site", "Site"),
        accessor: 'siteName',
        width: 100,
        sortable: true,
        render: ({siteId, siteName}) => (
          siteId ? <EntityAnchor value={{ objectId: siteId, objectName: siteName, objectType: "SITE"}} /> : undefined
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "afe"),
        title: t("common.entity.containercluster.table.column.afe", "AFE"),
        accessor: 'afe',
        width: 100,
        sortable: true,
      },
      {
        title: t("common.entity.containercluster.table.column.equalized", "Equalized"),
        accessor: 'equalized',
        width: 100,
        sortable: true,
        render: ({equalized}) => (
          equalized ? "Yes" : "No"
        )
      },
    ]

    return values
  }, [filterConfig, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type ContainerClusterDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<ContainerClusterDataTableProps, 'recordsPerPageOptions'>
}

export const ContainerClusterDataTableModal = (props: ContainerClusterDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.containercluster.plural", "Container Clusters"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <ContainerClusterDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
